import React, { useEffect, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import AdjustmentRequestService, {
  DebtAdjustmentRequest,
  ADJUSTMENT_REQUEST_STATUS,
  DEBT_REQUEST_TYPE_LABEL,
} from "../services/adjustment-request-service";
import { formatDateTime } from "../../fault-ticket/services/fault-ticket-service";

interface Props {
  open: boolean;
  request: DebtAdjustmentRequest | null;
  onClose: () => void;
  onUpdated: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-800 font-medium text-right">{value}</span>
  </div>
);

const REQUEST_TYPE_BADGE: Record<string, string> = {
  balance: "bg-violet-100 text-violet-700",
  backfill: "bg-sky-100 text-sky-700",
  debt: "bg-slate-100 text-slate-600",
};

/** Duyệt yêu cầu gọi thẳng `CustomerItemDebtService.adjust()` sẵn có ở BE — chỉ thêm bước xem/duyệt ở đây. */
const DebtAdjustmentDetailModal: React.FC<Props> = ({ open, request, onClose, onUpdated }) => {
  const { fetch: approve, loading: approving } = AdjustmentRequestService.approveDebt();
  const { fetch: reject, loading: rejecting } = AdjustmentRequestService.rejectDebt();
  const [reviewNote, setReviewNote] = useState("");

  useEffect(() => {
    setReviewNote("");
  }, [request?.id]);

  if (!open || !request) return null;
  const isPending = request.status === ADJUSTMENT_REQUEST_STATUS.PENDING;
  const items = request.items;

  const handleApprove = async () => {
    try {
      const res = await approve(request.id, { review_note: reviewNote || undefined });
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã duyệt yêu cầu điều chỉnh công nợ" });
        onUpdated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Duyệt thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Duyệt thất bại" });
    }
  };

  const handleReject = async () => {
    if (!reviewNote.trim()) {
      showToast({ type: "error", message: "Vui lòng nhập lý do từ chối" });
      return;
    }
    try {
      const res = await reject(request.id, reviewNote.trim());
      if (res && (res.status === 200 || res.status === undefined)) {
        showToast({ type: "success", message: "Đã từ chối yêu cầu" });
        onUpdated();
        onClose();
      } else {
        showToast({ type: "error", message: res?.message || "Từ chối thất bại" });
      }
    } catch (e: any) {
      showToast({ type: "error", message: e?.message || "Từ chối thất bại" });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[480px] max-w-[95vw] p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-slate-800">Yêu cầu điều chỉnh công nợ #{request.id}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700 text-lg">
            ✕
          </button>
        </div>
        <span
          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold mb-3 ${
            REQUEST_TYPE_BADGE[request.request_type] || REQUEST_TYPE_BADGE.debt
          }`}
        >
          {DEBT_REQUEST_TYPE_LABEL[request.request_type] || "Điều chỉnh"}
        </span>

        <div className="divide-y mb-4">
          <Row label="Khách hàng" value={request.customer_name || `#${request.customer_id}`} />
          <Row label="Lý do" value={request.reason} />
          <Row label="Người tạo" value={request.requester_name || `NV #${request.requested_by}`} />
          <Row label="Ngày tạo" value={formatDateTime(request.created_at)} />
        </div>

        <div className="mb-4">
          <div className="text-sm font-semibold text-slate-700 mb-2">
            Số lượng đề xuất {items.length > 1 ? `(${items.length} loại đồ)` : ""}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
            {items.map((it, idx) => (
              <div key={idx} className="flex justify-between px-3 py-2 text-sm">
                <span className="text-slate-700">{it.category}</span>
                <span className={it.quantity >= 0 ? "text-amber-600 font-semibold" : "text-green-600 font-semibold"}>
                  {it.quantity >= 0 ? `+${it.quantity}` : it.quantity} cái
                  {it.quantity >= 0 ? " (thêm nợ)" : " (trừ nợ)"}
                </span>
              </div>
            ))}
          </div>
        </div>

        {isPending ? (
          <>
            <textarea
              className="w-full border border-slate-200 rounded-lg p-2 text-sm mb-4"
              rows={2}
              placeholder="Ghi chú của giám sát (không bắt buộc khi duyệt, BẮT BUỘC khi từ chối)"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={handleReject}
                disabled={rejecting || approving}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold disabled:opacity-50"
              >
                Từ chối
              </button>
              <button
                onClick={handleApprove}
                disabled={rejecting || approving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold disabled:opacity-50"
              >
                {approving ? "Đang duyệt..." : "Duyệt"}
              </button>
            </div>
          </>
        ) : (
          <div className="divide-y">
            <Row label="Trạng thái" value={request.status === ADJUSTMENT_REQUEST_STATUS.APPROVED ? "Đã duyệt" : "Đã từ chối"} />
            <Row label="Người duyệt" value={request.reviewed_by ? request.reviewer_name || `NV #${request.reviewed_by}` : "—"} />
            {request.review_note && <Row label="Ghi chú" value={request.review_note} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default DebtAdjustmentDetailModal;
